import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  AccessTime as TimeIcon,
  PlayArrow as PlayIcon
} from '@mui/icons-material';
import { SchedulerStatus } from '@/types/smsScheduler';
import { formatErrorForDisplay } from '@/utils/errorUtils';

interface SchedulerStatusCardProps {
  status: SchedulerStatus | null;
  loading?: boolean;
  error?: string | null;
}

const SchedulerStatusCard: React.FC<SchedulerStatusCardProps> = ({
  status,
  loading = false,
  error = null
}) => {
  const formatNextRunTime = (nextRunTime: string): string => {
    try {
      const date = new Date(nextRunTime);
      const now = new Date();
      const diffMs = date.getTime() - now.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (diffMs < 0) {
        return 'Overdue';
      } else if (diffHours < 1) {
        return `${diffMinutes}m`;
      } else if (diffHours < 24) {
        return `${diffHours}h ${diffMinutes}m`;
      } else {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    } catch {
      return nextRunTime;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant="h6">Loading Scheduler Status...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            {formatErrorForDisplay(error)}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!status) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" color="text.secondary">
            No Scheduler Status Available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const activeJobs = status.jobs.filter(job => job.name.includes('sms'));
  const nextRunTimes = status.jobs.map(job => job.next_run_time).filter(Boolean);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <ScheduleIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
          <Typography variant="h6" component="h2">
            Scheduler Status
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Status Overview */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main" fontWeight="bold">
                {status.total_jobs}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Jobs
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {status.sms_jobs}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                SMS Jobs
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main" fontWeight="bold">
                {activeJobs.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Jobs
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main" fontWeight="bold">
                {nextRunTimes.length > 0 ? formatNextRunTime(nextRunTimes[0]) : 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Next Run
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Active Jobs List */}
        {activeJobs.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Active SMS Jobs
            </Typography>
            <List dense>
              {activeJobs.map((job, index) => (
                <React.Fragment key={job.id}>
                  <ListItem>
                    <ListItemIcon>
                      <PlayIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {job.name}
                          </Typography>
                          <Chip
                            label="Active"
                            size="small"
                            color="success"
                            icon={<ActiveIcon />}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <TimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            Next run: {formatNextRunTime(job.next_run_time)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < activeJobs.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}

        {activeJobs.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <InactiveIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No active SMS scheduler jobs
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SchedulerStatusCard;

